import type { AnalysisResult, Issue, IssueSeverity } from '../types';

const BRAND_LINE = 'Created by Richard Wong / The SEO Consultant.ai LLC';
const BRAND_URL = 'https://theseoconsultant.ai';
const TOOL_URL = 'https://semantix.theseoconsultant.ai';

function nowIso(): string {
  return new Date().toISOString();
}

function nowReadable(): string {
  return new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

function scoreLabel(s: number): string {
  if (s >= 90) return 'Excellent';
  if (s >= 70) return 'Needs improvement';
  return 'Poor semantics';
}

function severityOrder(s: IssueSeverity): number {
  return s === 'Critical' ? 0 : s === 'Warning' ? 1 : 2;
}

function csvEscape(value: unknown): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// ---------------------------------------------------------------------------
// Markdown
// ---------------------------------------------------------------------------

export function toMarkdown(r: AnalysisResult): string {
  const lines: string[] = [];

  lines.push('# Semantic HTML Analysis Report');
  lines.push('');
  if (r.sourceUrl) {
    const title = r.sourceTitle ? `**${r.sourceTitle}**` : '';
    lines.push(`${title}${title ? ' · ' : ''}${r.sourceUrl}`);
  }
  lines.push(`*Analyzed ${nowReadable()}*`);
  lines.push('');

  // Score
  lines.push(`## Score: ${r.score} / 100 — ${scoreLabel(r.score)}`);
  lines.push('');
  lines.push(`> ${r.summary}`);
  lines.push('');

  // Structural composition
  const sem = r.tagStats.semantic;
  const gen = r.tagStats.generic;
  const total = r.tagStats.semantic + r.tagStats.generic;
  const semPct = total > 0 ? Math.round((sem / total) * 100) : 0;
  const altCov = r.imagesTotal > 0
    ? Math.round(((r.imagesTotal - r.imagesWithoutAlt) / r.imagesTotal) * 100)
    : null;
  lines.push('## Structural composition');
  lines.push(`- Semantic tags: ${sem}`);
  lines.push(`- Generic tags (div/span): ${gen}`);
  lines.push(`- Semantic ratio: ${semPct}%`);
  lines.push(`- Text-to-HTML ratio: ${Math.round((r.textHtmlRatio || 0) * 100)}%`);
  if (r.imagesTotal > 0 && altCov !== null) {
    lines.push(`- Image alt coverage: ${r.imagesTotal - r.imagesWithoutAlt} / ${r.imagesTotal} (${altCov}%)`);
  }
  lines.push('');

  // Heading hierarchy
  lines.push('## Heading hierarchy');
  if (r.headingHierarchy.length === 0) {
    lines.push('_No headings found._');
  } else {
    for (const h of r.headingHierarchy) {
      const level = parseInt(h.tag.slice(1), 10);
      const indent = '  '.repeat(Math.max(0, level - 1));
      const skipFlag = h.skipped ? ' _(skipped level)_' : '';
      const text = h.text || '_(empty)_';
      lines.push(`${indent}- **${h.tag.toUpperCase()}** ${text}${skipFlag}`);
    }
  }
  lines.push('');

  // Schema
  lines.push('## Structured data');
  if (r.schemaTypes.length === 0) {
    lines.push('_No JSON-LD structured data detected._');
  } else {
    lines.push(r.schemaTypes.map((t) => `\`${t}\``).join(', '));
  }
  lines.push('');

  // Meta coverage
  lines.push('## Meta coverage');
  const m = r.metaCoverage;
  lines.push(`- ${m.viewport ? '✓' : '✗'} Viewport`);
  lines.push(`- ${m.canonical ? '✓' : '✗'} Canonical URL`);
  lines.push(`- ${m.description ? '✓' : '✗'} Meta description`);
  lines.push(`- ${m.openGraph ? '✓' : '✗'} Open Graph`);
  lines.push(`- ${m.twitterCard ? '✓' : '✗'} Twitter Card`);
  lines.push('');

  // Render diff
  if (r.renderDiff) {
    lines.push('## Pre-render vs post-render');
    if (!r.renderDiff.ssrAvailable) {
      lines.push('_SSR view unavailable — Cloudflare or similar bot protection blocked the non-JS fetch._');
    } else {
      const d = r.renderDiff;
      const issues: string[] = [];
      if (d.schemaTypesAddedByJs.length) issues.push(`- **Schema added by JS only:** ${d.schemaTypesAddedByJs.join(', ')} _(invisible to non-rendering crawlers)_`);
      if (d.schemaTypesRemovedByJs.length) issues.push(`- **Schema removed by JS:** ${d.schemaTypesRemovedByJs.join(', ')}`);
      if (d.h1AddedByJs) issues.push('- **H1 only appears after JS** — page has no topic signal pre-render');
      if (d.titleAddedByJs) issues.push('- **<title> only set by JS**');
      if (d.metaAddedByJs.canonical) issues.push('- **Canonical URL only set by JS**');
      if (d.metaAddedByJs.description) issues.push('- **Meta description only set by JS**');
      if (d.metaAddedByJs.openGraph) issues.push('- **Open Graph tags only set by JS**');
      if (d.metaAddedByJs.viewport) issues.push('- **Viewport meta only set by JS**');
      if (d.metaAddedByJs.twitterCard) issues.push('- **Twitter Card tags only set by JS**');
      const headDelta = d.headingsCountCsr - d.headingsCountSsr;
      if (headDelta > 5) issues.push(`- **${headDelta} headings only appear after JS** (SSR ${d.headingsCountSsr} vs CSR ${d.headingsCountCsr})`);

      if (issues.length === 0) {
        lines.push('✓ Server-rendered output already includes all SEO-critical content.');
      } else {
        lines.push(`SSR: ${(d.ssrSize / 1000).toFixed(0)}k chars · CSR: ${(d.csrSize / 1000).toFixed(0)}k chars`);
        lines.push('');
        lines.push(...issues);
      }
    }
    lines.push('');
  }

  // Issues
  lines.push('## Issues');
  lines.push('');
  if (r.issues.length === 0) {
    lines.push('_No issues found._');
  } else {
    const sorted = [...r.issues].sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
    let lastSeverity: IssueSeverity | null = null;
    for (const issue of sorted) {
      if (issue.severity !== lastSeverity) {
        if (lastSeverity !== null) lines.push('');
        lines.push(`### ${issue.severity}`);
        lines.push('');
        lastSeverity = issue.severity;
      }
      const lineRef = issue.lineNumber && issue.lineNumber > 0 ? ` _(line ${issue.lineNumber})_` : '';
      lines.push(`#### \`${issue.element}\`${lineRef}`);
      lines.push(issue.description);
      lines.push('');
      lines.push(`**Fix:** ${issue.suggestion}`);
      lines.push('');
    }
  }

  // Positive highlights
  if (r.positiveHighlights.length > 0) {
    lines.push('## Doing well');
    for (const h of r.positiveHighlights) {
      lines.push(`- ✓ ${h}`);
    }
    lines.push('');
  }

  // Brand footer
  lines.push('---');
  lines.push('');
  lines.push(`*${BRAND_LINE} — [${BRAND_URL}](${BRAND_URL})*  `);
  lines.push(`*Tool: [${TOOL_URL}](${TOOL_URL})*`);
  lines.push('');

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// JSON
// ---------------------------------------------------------------------------

export function toJson(r: AnalysisResult): string {
  const payload = {
    _meta: {
      tool: 'Semantic HTML Analyzer',
      createdBy: BRAND_LINE,
      brandUrl: BRAND_URL,
      toolUrl: TOOL_URL,
      exportedAt: nowIso(),
    },
    result: r,
  };
  return JSON.stringify(payload, null, 2);
}

// ---------------------------------------------------------------------------
// CSV (issues only)
// ---------------------------------------------------------------------------

export function toIssuesCsv(r: AnalysisResult): string {
  const headers = ['Severity', 'Element', 'Line', 'Description', 'Fix'];
  const sorted = [...r.issues].sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
  const rows: string[][] = [headers];
  for (const i of sorted) {
    rows.push([
      i.severity,
      i.element,
      String(i.lineNumber ?? 0),
      i.description,
      i.suggestion,
    ]);
  }
  // CSV body
  const body = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
  // Branded comment line at top (Excel/Sheets ignore lines starting with #
  // only if a delimiter is specified; safer to embed as a trailing footer row)
  const meta = `# ${BRAND_LINE} — ${BRAND_URL}`;
  const exportedAt = `# Exported: ${nowIso()}`;
  const url = r.sourceUrl ? `# Source URL: ${r.sourceUrl}\n` : '';
  return `${meta}\n${exportedAt}\n${url}${body}\n`;
}

// ---------------------------------------------------------------------------
// Browser helpers
// ---------------------------------------------------------------------------

export function triggerDownload(filename: string, mimeType: string, content: string): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function suggestedFilename(r: AnalysisResult, ext: string): string {
  const date = new Date().toISOString().slice(0, 10);
  let slug = 'analysis';
  if (r.sourceUrl) {
    try {
      const u = new URL(r.sourceUrl);
      slug = (u.hostname + u.pathname)
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
        .toLowerCase();
    } catch {
      // ignore
    }
  }
  return `semantic-html-${slug}-${date}.${ext}`;
}
